import React, { useEffect, useMemo, useState } from "react";
import { recordView } from "@/lib/recent";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapPin, CalendarDays, Clock, Accessibility, ArrowLeft, Minus, Plus, Ticket, Share2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { deviceId } from "@/lib/api";
import { eventsApi, pence, venueTime } from "@/lib/eventsApi";
import { CreatorAvatar } from "@/components/stage/PostCard";

function Qty({ value, max, onChange, disabled }) {
  return (
    <div className="inline-flex items-center rounded-full border border-border">
      <button type="button" aria-label="Fewer" disabled={disabled || value <= 0} onClick={() => onChange(value - 1)} className="p-2 disabled:opacity-40"><Minus size={14} /></button>
      <span className="w-6 text-center font-mono-num text-sm font-semibold">{value}</span>
      <button type="button" aria-label="More" disabled={disabled || value >= max} onClick={() => onChange(value + 1)} className="p-2 disabled:opacity-40"><Plus size={14} /></button>
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ev, setEv] = useState(undefined);
  useEffect(() => { if (ev && ev.id) recordView({ type: "event", id: ev.id, title: ev.title, image: ev.cover_url, url: "/events/" + ev.id }); }, [ev]);
  const [qty, setQty] = useState({});
  const [promo, setPromo] = useState("");
  const [access, setAccess] = useState("");
  const [hold, setHold] = useState(null);
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);

  usePageMeta(ev?.title || "Event", ev?.description?.slice(0, 150) || "An event on Fable +.");

  useEffect(() => {
    let alive = true;
    setEv(undefined);
    eventsApi.get(id).then((d) => alive && setEv(d)).catch((e) => alive && setEv({ error: e?.status === 404 ? "This event isn't available." : e?.message || "Couldn't load this event." }));
    return () => { alive = false; };
  }, [id, user?.id]);

  // hold countdown
  useEffect(() => {
    if (!hold) return undefined;
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(hold.expires_at).getTime() - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [hold]);

  const selected = useMemo(() => Object.entries(qty).filter(([, n]) => n > 0).map(([ticket_type_id, n]) => ({ ticket_type_id, qty: n })), [qty]);
  const subtotal = useMemo(() => (ev?.ticket_types || []).reduce((s, t) => s + (qty[t.id] || 0) * t.price_p, 0), [ev, qty]);

  const startHold = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/events/${id}` } });
      return;
    }
    setBusy(true);
    try {
      const extra = {};
      if (promo.trim()) extra.promo_code = promo.trim();
      if (access.trim()) extra.access_code = access.trim();
      const h = await eventsApi.hold(id, selected, extra);
      setHold(h);
      setAccept(false);
    } catch (e) {
      toast.error(e.message || "Couldn't hold those tickets.");
    } finally {
      setBusy(false);
    }
  };
  const releaseHold = async () => {
    if (hold) eventsApi.dropHold(hold.id).catch(() => null);
    setHold(null);
  };
  const pay = async () => {
    if (!hold) return;
    setBusy(true);
    try {
      const r = await eventsApi.checkout(hold.id, { accept_terms: accept, return_base: window.location.origin, device_id: deviceId() });
      if (r.free) {
        toast.success("You're in — tickets are in My tickets.");
        navigate(`/events/order-return?order_id=${r.order_id}&free=1`);
        return;
      }
      if (r.url) {
        window.location.assign(r.url);
        return;
      }
      toast.error("Couldn't start checkout.");
    } catch (e) {
      toast.error(e.message || "Couldn't start checkout.");
      if (/hold ran out/i.test(e.message || "")) setHold(null);
    } finally {
      setBusy(false);
    }
  };
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: ev.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch { /* cancelled */ }
  };

  if (ev === undefined) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Skeleton className="aspect-[16/7] rounded-3xl" />
        <Skeleton className="mt-6 h-10 w-2/3" />
        <Skeleton className="mt-3 h-5 w-1/3" />
      </div>
    );
  }
  if (ev.error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
        <p className="font-semibold">{ev.error}</p>
        <Link to="/events"><Button variant="outline" className="mt-5 rounded-full"><ArrowLeft size={16} className="mr-1.5" /> All events</Button></Link>
      </div>
    );
  }

  const onSale = ev.status === "on_sale";
  const canBuy = onSale && !ev.is_host && selected.length > 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6" data-testid={`event-detail-${ev.id}`}>
      <Link to="/events" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Events</Link>
      <div className="relative mt-4 aspect-[16/7] overflow-hidden rounded-3xl bg-muted">
        {(() => {
          const slides = [...(ev.cover_url ? [{ type: "image", url: ev.cover_url }] : []), ...((ev.media || []).filter((m) => m && m.url && m.url !== ev.cover_url))];
          if (!slides.length) return null;
          return (
            <div className="flex h-full w-full snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none]" data-testid="event-media-carousel">
              {slides.map((m, i) => (
                <div key={i + m.url} className="relative h-full w-full shrink-0 snap-center">
                  {m.type === "video"
                    ? <video src={m.url} className="h-full w-full object-cover" muted loop playsInline autoPlay={i === 0} controls={i !== 0} />
                    : <img src={m.url} alt="" className="h-full w-full object-cover" loading={i ? "lazy" : "eager"} />}
                  {slides.length > 1 && <span className="absolute bottom-3 right-3 rounded-full bg-black/45 px-2.5 py-1 text-xs font-semibold text-white">{i + 1} / {slides.length}</span>}
                </div>
              ))}
            </div>
          );
        })()}
        {ev.status !== "on_sale" && <span className="absolute left-4 top-4 rounded-full bg-foreground px-3 py-1 text-xs font-semibold capitalize text-background">{ev.status.replace("_", " ")}</span>}
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div>
          <p className="text-sm font-semibold capitalize text-primary">{ev.category}{ev.age_rating === "18_plus" ? " · 18+" : ""}</p>
          <h1 className="mt-1 font-serif-display text-3xl font-bold leading-tight sm:text-4xl">{ev.title}</h1>
          <div className="mt-4 space-y-2 text-[15px]">
            <p className="flex items-center gap-2"><CalendarDays size={17} className="text-muted-foreground" /> {venueTime(ev.starts_at)}{ev.ends_at ? ` – ${venueTime(ev.ends_at, { date: false })}` : ""}</p>
            {ev.doors_at && <p className="flex items-center gap-2"><Clock size={17} className="text-muted-foreground" /> Doors {venueTime(ev.doors_at, { date: false })}</p>}
            <p className="flex items-center gap-2"><MapPin size={17} className="text-muted-foreground" /> {ev.online ? "Online event" : [ev.venue?.name, ev.venue?.address, ev.venue?.city, ev.venue?.postcode].filter(Boolean).join(", ")}</p>
            {(ev.accessibility || ev.venue?.accessibility) && <p className="flex items-center gap-2"><Accessibility size={17} className="text-muted-foreground" /> {ev.accessibility || ev.venue?.accessibility}</p>}
          </div>
          {ev.host && (
            <div className="mt-5 flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
              <CreatorAvatar author={{ display_name: ev.host.name, avatar_url: ev.host.avatar }} size={40} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">Hosted by</p>
                {ev.host.handle ? <Link to={`/@${ev.host.handle}`} className="font-semibold hover:underline">{ev.host.name}</Link> : <p className="font-semibold">{ev.host.name}</p>}
              </div>
              <Button variant="outline" size="sm" className="rounded-full" onClick={share}><Share2 size={14} className="mr-1.5" /> Share</Button>
            </div>
          )}
          {ev.description && <p className="mt-6 max-w-2xl whitespace-pre-wrap text-[15px] leading-relaxed">{ev.description}</p>}
          {Array.isArray(ev.friends_going) && ev.friends_going.length > 0 && (
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground"><Users size={15} /> {ev.friends_going.length} people you follow are going</p>
          )}
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <a href={eventsApi.calendarUrl(ev.id)} className="rounded-full border border-border px-4 py-1.5 font-medium hover:bg-muted">Add to calendar</a>
            {ev.my_tickets > 0 && <Link to="/tickets" className="rounded-full border border-border px-4 py-1.5 font-medium hover:bg-muted">You have {ev.my_tickets} ticket{ev.my_tickets > 1 ? "s" : ""}</Link>}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="ticket-box">
            <div className="flex items-baseline justify-between">
              <h2 className="font-bold">Tickets</h2>
              <span className="text-sm font-semibold">{ev.price_line}</span>
            </div>
            {ev.fee_line && <p className="mt-1 text-xs text-muted-foreground">{ev.fee_line}</p>}
            {!hold ? (
              <>
                <ul className="mt-4 space-y-3">
                  {(ev.ticket_types || []).map((t) => {
                    const dead = t.sold_out || !t.on_sale_now || !onSale;
                    return (
                      <li key={t.id} className="flex items-start justify-between gap-3" data-testid={`ticket-type-${t.id}`}>
                        <div className="min-w-0">
                          <p className="font-medium">{t.name} <span className="font-mono-num text-muted-foreground">· {t.price_p ? pence(t.price_p) : "Free"}</span></p>
                          {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                          <p className="text-xs text-muted-foreground">
                            {t.sold_out ? "Sold out" : !t.on_sale_now ? (t.opens_at ? `On sale ${venueTime(t.opens_at)}` : "Not on sale") : t.left != null && t.left <= 10 ? `${t.left} left` : `Up to ${t.max_per_order} per order`}
                            {t.member_early ? " · Members' early access" : ""}
                          </p>
                        </div>
                        <Qty value={qty[t.id] || 0} max={Math.min(t.max_per_order, t.left ?? t.max_per_order)} disabled={dead} onChange={(n) => setQty((q) => ({ ...q, [t.id]: n }))} />
                      </li>
                    );
                  })}
                </ul>
                {ev.has_hidden && (
                  <input value={access} onChange={(e) => setAccess(e.target.value)} placeholder="Access code" className="mt-4 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" data-testid="access-code" />
                )}
                {ev.has_promos && (
                  <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Promo code" className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" data-testid="promo-code" />
                )}
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tickets</span>
                  <span className="font-mono-num font-semibold">{subtotal ? pence(subtotal) : "Free"}</span>
                </div>
                <Button className="mt-3 w-full rounded-full" disabled={!canBuy || busy} onClick={startHold} data-testid="hold-btn">
                  <Ticket size={16} className="mr-1.5" /> {ev.is_host ? "You're the host" : !onSale ? (ev.status === "sold_out" ? "Sold out" : "Not on sale") : busy ? "Holding…" : user ? "Get tickets" : "Sign in to get tickets"}
                </Button>
                {ev.cancel_line && <p className="mt-3 text-xs text-muted-foreground">{ev.cancel_line}</p>}
              </>
            ) : (
              <div className="mt-4" data-testid="hold-box">
                <p className="rounded-xl bg-muted px-3 py-2 text-sm">Held for <span className="font-mono-num font-semibold">{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span> — finish checkout to keep them.</p>
                <ul className="mt-3 space-y-1 text-sm">
                  {hold.items.map((it) => (
                    <li key={it.ticket_type_id} className="flex justify-between"><span>{it.qty} × {it.name}</span><span className="font-mono-num">{it.price_p ? pence(it.price_p * it.qty) : "Free"}</span></li>
                  ))}
                  {hold.promo && <li className="flex justify-between text-emerald-700"><span>{hold.promo.label || hold.promo.code}</span><span className="font-mono-num">−{pence(hold.promo.discount_p)}</span></li>}
                  {hold.price?.fee > 0 && <li className="flex justify-between text-muted-foreground"><span>Service fee (per order)</span><span className="font-mono-num">{pence(hold.price.fee)}</span></li>}
                  <li className="flex justify-between border-t border-border pt-2 font-semibold"><span>Total</span><span className="font-mono-num">{hold.price?.total ? pence(hold.price.total) : "Free"}</span></li>
                </ul>
                {hold.copy?.how && <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{hold.copy.how}</p>}
                <label className="mt-3 flex items-start gap-2 text-xs">
                  <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5" data-testid="accept-terms" />
                  <span>I understand when I'll be charged and accept the refund policy{hold.policy_text ? ` (${hold.policy_text})` : ""}.</span>
                </label>
                <Button className="mt-3 w-full rounded-full" disabled={!accept || busy || left === 0} onClick={pay} data-testid="pay-btn">
                  {busy ? "One moment…" : hold.price?.total ? `Pay ${pence(hold.price.total)}` : "Confirm free tickets"}
                </Button>
                <button type="button" onClick={releaseHold} className="mt-2 w-full text-xs text-muted-foreground underline">Change tickets</button>
                {hold.price?.total > 0 && <p className="mt-2 text-center text-[11px] text-muted-foreground">Test payments · card details go to Stripe, never to Fable +.</p>}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
